import { Box, Checkbox, Label, Link, Paragraph } from '@alto-avios/alto-ui';
import { useFormContext, Controller } from 'react-hook-form';
import { isValidTermsAndConditions } from '@/utils/validations';
import { useTranslation } from '@/utils/useTranslation';
import { Programmes } from '@/utils/constants';
import { SectionHeading } from '@/components/SectionHeading';
import { useGlobalContext } from '@/components/GlobalProvider';
import { Children, useCallback, type ReactNode } from 'react';

const renderExternalLink =
  (key: string, href: string) => (chunks: ReactNode) => (
    <Link
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      iconEndProps={false}
    >
      {chunks}
    </Link>
  );

export const TermsAndConditions = () => {
  const { programmeId } = useGlobalContext();
  const { control } = useFormContext();
  const { t, tRich } = useTranslation('termsAndConditions');
  const termsAndConditionsMap = {
    [Programmes.BAEC]: t('links.terms_baec'),
    [Programmes.IBP]: t('links.terms_ibp'),
    [Programmes.AERC]: t('links.terms_aerc'),
  };

  const getSendNewsLabel = useCallback((): ReactNode => {
    if (programmeId === Programmes.AERC) {
      return (
        <>
          {t('sendNews.title')}
          {'. '}
          {tRich('sendNews.description_aerc', {
            privacyLink: (chunks: ReactNode) => (
              <Link
                href={t('links.privacy_statement')}
                target="_blank"
                rel="noopener noreferrer"
                iconEndProps={false}
              >
                {chunks}
              </Link>
            ),
          })}
        </>
      );
    }

    return t('sendNews.title');
  }, [programmeId, t, tRich]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={programmeId === Programmes.BAEC ? 'sm' : '2xs'}
    >
      <SectionHeading title={t('title')} />

      {programmeId === Programmes.BAEC ? (
        <>
          <Paragraph size="sm">{t('marketing.introduction')}</Paragraph>

          <Box display="flex" flexDirection="column" gap="2xs">
            <Paragraph size="sm">{t('marketing.description')}</Paragraph>

            <Controller
              name="marketingOptOut"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  data-testid="marketingOptOut-checkbox"
                  id="marketingOptOut"
                  label={<Label>{t('marketing.optOut')}</Label>}
                  isSelected={value}
                  onChange={onChange}
                />
              )}
            />
          </Box>
        </>
      ) : (
        <>
          <Controller
            name="sendNews"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                data-testid="sendNews-checkbox"
                id="sendNews"
                label={getSendNewsLabel()}
                isSelected={value}
                onChange={onChange}
              />
            )}
          />

          <Controller
            name="shareInformation"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                data-testid="shareInformation-checkbox"
                id="shareInformation"
                label={t('shareInformation')}
                isSelected={value}
                onChange={onChange}
              />
            )}
          />
        </>
      )}

      <Controller
        name="acceptTerms"
        control={control}
        rules={{
          required: t('fieldRequired'),
          validate: isValidTermsAndConditions,
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Checkbox
            data-testid="acceptTerms-checkbox"
            id="acceptTerms"
            label={
              <Label>
                {Children.toArray(
                  tRich('acceptTerms', {
                    aviosLink: renderExternalLink(
                      'avios-terms-and-conditions-link',
                      t('links.terms_avios'),
                    ),
                    link: renderExternalLink(
                      'terms-and-conditions-link',
                      programmeId ? termsAndConditionsMap[programmeId] : '',
                    ),
                    privacyLink: renderExternalLink(
                      'privacy-policy-link',
                      t('links.privacy_baec'),
                    ),
                  }),
                )}
              </Label>
            }
            isSelected={value}
            onChange={onChange}
            isInvalid={!!error}
            errorMessage={error?.message}
          />
        )}
      />
    </Box>
  );
};
